/**
 * useLocationParam Hook
 *
 * Persists the selected location ID in localStorage.
 * This allows location selection to persist when navigating between pages.
 *
 * Supports locationId=0 for Consolidated view (Admin/Executive/GM only).
 * Validates stored location against DepartmentManager assignments to prevent
 * loading data for unauthorized locations. A TSM with no resolved location
 * receives -1 as a sentinel; no API accepts that value.
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';

const DEFAULT_LOCATION_ID = 1;
const STORAGE_KEY = 'selectedLocationId';

interface UseLocationParamReturn {
  /** Current location ID (0 = Consolidated, > 0 = individual location) */
  locationId: number;
  /** Update location ID (also persists to localStorage) */
  setLocationId: (id: number) => void;
}

/**
 * Get location ID from localStorage, with fallback to default
 * Returns stored value if valid (>= 0), otherwise returns default
 */
function getStoredLocationId(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      // Allow 0 (Consolidated) and positive location IDs
      if (!isNaN(parsed) && parsed >= 0) {
        return parsed;
      }
    }
  } catch {
    // localStorage not available (e.g., private browsing)
  }
  return DEFAULT_LOCATION_ID;
}

/**
 * Validate a location ID against the user's role and assignments.
 * DepartmentManagers can only access their assigned locations.
 */
export function getValidLocationId(
  storedId: number,
  userRole: string | null,
  assignedLocationIds: number[]
): number {
  // Fail closed when the TSM's store service group did not resolve.
  if (userRole === 'TeamServiceManager' && assignedLocationIds.length === 0) {
    return -1;
  }

  const isLocationLimited = userRole === 'DepartmentManager' || userRole === 'TeamServiceManager';
  const hasAssignments = assignedLocationIds && assignedLocationIds.length > 0;

  if (isLocationLimited && hasAssignments) {
    if (assignedLocationIds.includes(storedId)) {
      return storedId;
    }
    return assignedLocationIds[0];
  }

  return storedId;
}

/**
 * Hook to manage location ID with localStorage persistence
 *
 * @example
 * const { locationId, setLocationId } = useLocationParam();
 * // On Forecast page: select Location 2
 * setLocationId(2);
 * // Navigate to Projections page: locationId is still 2
 */
export function useLocationParam(): UseLocationParamReturn {
  const { userRole, assignedLocationIds } = useAuth();

  // Initialize from localStorage, validated against user assignments
  const [locationId, setLocationIdState] = useState<number>(() => {
    const stored = getStoredLocationId();
    return getValidLocationId(stored, userRole, assignedLocationIds);
  });

  // Re-validate when auth data changes (e.g., assignments loaded after initial render)
  useEffect(() => {
    const validId = getValidLocationId(locationId, userRole, assignedLocationIds);
    if (validId !== locationId) {
      setLocationIdState(validId);
      try {
        if (validId !== -1) {
          localStorage.setItem(STORAGE_KEY, validId.toString());
        }
      } catch {
        // localStorage not available
      }
    }
  }, [userRole, assignedLocationIds, locationId]);

  // Sync with other tabs/windows, validating against assignments
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const parsed = parseInt(e.newValue, 10);
        if (!isNaN(parsed) && parsed >= 0) {
          const validId = getValidLocationId(parsed, userRole, assignedLocationIds);
          setLocationIdState(validId);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [userRole, assignedLocationIds]);

  // Update both state and localStorage
  const setLocationId = useCallback((id: number) => {
    setLocationIdState(id);
    if (id !== -1) {
      try {
        localStorage.setItem(STORAGE_KEY, id.toString());
      } catch {
        // localStorage not available
      }
    }
  }, []);

  return { locationId, setLocationId };
}
